import React, { useState, useEffect, Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { FiUsers, FiRefreshCw, FiHome, FiClock, FiStar } from 'react-icons/fi';
import { calculateVibe } from '../utils/helpers';
import supabase from '../supabase/supabase';
import { Footer } from '../components/Footer';
import { InviteSystem } from '../components/InviteSystem';
import { ErrorBoundary } from '../components/ErrorBoundary';

// Lazy load the SDK component
const SafeGetStarted = React.lazy(() => import('../components/SafeGetStarted'));

export const Dashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const creatorName = location.state?.creatorName || localStorage.getItem('dykm_creator_name') || "Player";
  const answers = location.state?.answers || JSON.parse(localStorage.getItem('dykm_answers') || '[]');
  const shareCode = location.state?.shareCode || localStorage.getItem('dykm_my_quiz_code');
  
  const [friends, setFriends] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (location.state?.shareCode) {
      localStorage.setItem('dykm_my_quiz_code', location.state.shareCode);
      localStorage.setItem('dykm_creator_name', location.state.creatorName);
      localStorage.setItem('dykm_answers', JSON.stringify(location.state.answers));
    }
  }, [location.state]);

  const fetchScores = async () => {
    if (!shareCode) return;
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('dykm_scores_1774355620612')
        .select('*')
        .eq('share_code', shareCode)
        .order('created_at', { ascending: false });

      if (!error && data) {
        setFriends(data);
      }
    } catch (err) {
      console.error("Dashboard fetch failed:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!shareCode) {
      navigate('/');
      return;
    }
    fetchScores();

    const channel = supabase
      .channel(`dashboard-${shareCode}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'dykm_scores_1774355620612', filter: `share_code=eq.${shareCode}` },
        (payload) => {
          setFriends(prev => [payload.new, ...prev]);
        }
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [shareCode, navigate]);

  const vibe = calculateVibe(answers);
  const shareUrl = `${window.location.origin}/#/challenge/${shareCode}`;

  return (
    <div className="min-h-screen flex flex-col relative bg-[#012b58]">
      <div className="flex-1 w-full max-w-md mx-auto p-6 pb-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6 pt-8">
          
          {/* Vibe Profile */}
          <div className="glass-panel rounded-3xl p-8 text-center relative overflow-hidden ring-1 ring-white/10">
            <div className={`absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r ${vibe.color}`} />
            <p className="text-slate-400 text-xs font-bold uppercase tracking-[0.2em] mb-3">Your Profile Created</p>
            <h2 className={`text-3xl font-black bg-clip-text text-transparent bg-gradient-to-r ${vibe.color} mb-4 uppercase`}>
              {vibe.title}
            </h2>
            <p className="text-slate-300 text-sm leading-relaxed">{vibe.desc}</p>
          </div>

          {/* Quest SDK: Safely Loaded */}
          {shareCode && (
            <div className="rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
              <ErrorBoundary fallback={
                <div className="p-6 text-center text-slate-400 bg-[#1a1a1a] text-xs">
                  Next steps tracker is running in fallback mode.
                </div>
              }>
                <Suspense fallback={<div className="p-6 text-center text-slate-400 bg-[#1a1a1a] text-xs">Loading tracker...</div>}>
                  <SafeGetStarted
                    questId="dykm-onboarding"
                    userId={shareCode}
                    token="dummy-token"
                    styleConfig={{
                      Form: { backgroundColor: '#1a1a1a', padding: '24px' },
                      Topbar: { borderBottom: '1px solid #333333' },
                      Heading: { color: '#ffffff', fontSize: '18px', fontWeight: '800' },
                      Description: { color: '#94a3b8', fontSize: '13px' },
                      Card: { backgroundColor: '#012b58', border: '1px solid #1e293b', borderRadius: '16px' },
                      PrimaryButton: { backgroundColor: '#0094f7', color: '#ffffff', borderRadius: '12px' },
                      ProgressBar: { 
                        barColor: '#d2ff02', 
                        barParentColor: '#334155',
                        ProgressText: { color: '#ffffff' }
                      }
                    }}
                  />
                </Suspense>
              </ErrorBoundary>
            </div>
          )}

          {/* Invite System */}
          <InviteSystem shareUrl={shareUrl} creatorName={creatorName} />

          {/* Friend Scores */}
          <div className="bg-white/5 rounded-3xl p-6 border border-white/10 shadow-xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold flex items-center gap-2 text-white">
                <SafeIcon icon={FiUsers} className="text-[#d2ff02]" /> 
                Live Leaderboard
              </h3>
              <button onClick={fetchScores} className="p-2 hover:bg-white/5 rounded-lg text-[#0094f7]">
                <SafeIcon icon={FiRefreshCw} className={isLoading ? 'animate-spin' : ''} />
              </button>
            </div>

            {isLoading && friends.length === 0 ? (
              <div className="text-center py-10 text-slate-500">
                <div className="w-6 h-6 border-2 border-[#0094f7] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                Syncing scores...
              </div>
            ) : friends.length === 0 ? (
              <div className="text-center py-12 text-slate-500 border-2 border-dashed border-white/5 rounded-3xl">
                <SafeIcon icon={FiClock} className="text-4xl mx-auto mb-3 opacity-20" />
                <p className="text-sm font-medium">No one has taken the challenge yet.</p>
                <p className="text-[10px] uppercase mt-2 tracking-widest font-bold">Share your link to start!</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto custom-scrollbar pr-2">
                {friends.map((friend, idx) => (
                  <motion.div 
                    key={friend.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="flex justify-between items-center p-4 bg-white/5 rounded-2xl border border-white/5 hover:border-[#0094f7]/30 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black ${idx === 0 ? 'bg-[#d2ff02] text-[#012b58]' : 'bg-slate-800 text-slate-400'}`}>
                        {idx + 1}
                      </div>
                      <span className="font-bold text-slate-200">{friend.challenger_name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                       <span className={`text-sm font-black ${friend.score >= 5 ? 'text-[#d2ff02]' : 'text-slate-400'}`}>
                        {friend.score}/10
                      </span>
                      {friend.score >= 8 && <SafeIcon icon={FiStar} className="text-[#d2ff02]" />}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-3">
            <Button variant="ghost" className="w-full text-xs font-bold uppercase tracking-widest opacity-50 hover:opacity-100" onClick={() => navigate('/')}>
              <SafeIcon icon={FiHome} /> Back to Start
            </Button>
          </div>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};